import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import PixAndGoHeader from "@/components/molecules/PixAndGoHeader";
import PixAndGoLayout from "@/components/molecules/PixAndGoLayout";
import PixAndGoStepper from "@/components/molecules/PixAndGoStepper";
import { ReactComponent as ErrorIcon } from "@/assets/images/error.svg";
import i18n from "@/translate/i18n";
import { getPixAndGoStatus } from "@/services/pixAndGoApi";
import usePixAndGoStatusSync from "@/hooks/usePixAndGoStatusSync";
import {
  PIX_AND_GO_FINAL_STATUSES,
  PIX_AND_GO_FORM_REQUIRED_STATUS,
  PIX_AND_GO_PROCESSING_STATUSES,
} from "@/utils/pixAndGoStatus";

const CONFIRM_GRACE_PERIOD_MS = 30000;

function PixAndGoProcessing() {
  const history = useHistory();
  const location = useLocation();
  const { uuid } = useParams();

  const merchantSlug = location.state?.merchantSlug;
  const initialStatus = location.state?.status || "registering_user";

  const [currentStatus, setCurrentStatus] = useState(initialStatus);
  const [confirmTimedOut, setConfirmTimedOut] = useState(false);

  useEffect(() => {
    i18n.changeLanguage("pt-BR");
  }, []);

  const handleStatusUpdate = useCallback(
    (status) => {
      if (PIX_AND_GO_FINAL_STATUSES.includes(status)) {
        history.push({
          pathname: `/pix-and-go/${uuid}/status`,
          state: { status, merchantSlug },
        });
        return;
      }
      if (status === PIX_AND_GO_FORM_REQUIRED_STATUS) {
        history.push({
          pathname: `/pix-and-go/${uuid}/form`,
          state: { merchantSlug },
        });
        return;
      }
      if (PIX_AND_GO_PROCESSING_STATUSES.includes(status)) {
        setCurrentStatus(status);
      }
    },
    [history, uuid, merchantSlug],
  );

  useEffect(() => {
    getPixAndGoStatus(uuid)
      .then((res) => handleStatusUpdate(res.data.status))
      .catch(() => {
        // Transient error — stay on page and let websocket/polling handle final state
      });
  }, [uuid, handleStatusUpdate]);

  const handleConfirmTimeout = useCallback(() => {
    setConfirmTimedOut(true);
  }, []);

  const { checkNow } = usePixAndGoStatusSync(uuid, {
    onStatus: handleStatusUpdate,
    onExhausted: handleConfirmTimeout,
    autoStart: true,
    gracePeriodMs: CONFIRM_GRACE_PERIOD_MS,
  });

  const handleRetry = useCallback(() => {
    setConfirmTimedOut(false);
    checkNow();
  }, [checkNow]);

  if (confirmTimedOut) {
    return (
      <PixAndGoLayout merchantSlug={merchantSlug}>
        <PixAndGoHeader />
        <PixAndGoStepper currentStep={4} />
        <main className="flex flex-col items-center justify-center flex-1 px-6 pb-12 sm:px-12">
          <div className="w-full max-w-lg text-center flex flex-col items-center gap-6">
            <ErrorIcon className="w-24 h-24" />
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-pag-danger">
                {i18n.t("pixAndGo.processing.confirmTimeoutTitle")}
              </h1>
              <p className="text-base text-pag-on-background-muted leading-relaxed">
                {i18n.t("pixAndGo.processing.confirmTimeoutMessage")}
              </p>
            </div>
            <div className="flex flex-col gap-3 w-full">
              <button
                type="button"
                onClick={handleRetry}
                className="w-full py-4 rounded-full bg-pag-primary text-pag-on-primary font-bold text-base"
              >
                {i18n.t("pixAndGo.status.retry")}
              </button>
              {process.env.REACT_APP_SUPPORT_URL && (
                <a
                  href={process.env.REACT_APP_SUPPORT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-3 rounded-full border-2 border-pag-danger text-pag-danger font-semibold text-base flex items-center justify-center"
                >
                  {i18n.t("pixAndGo.qr.errorSupport")}
                </a>
              )}
            </div>
          </div>
        </main>
      </PixAndGoLayout>
    );
  }

  const isDeposit = currentStatus === "deposit_in_progress";

  return (
    <PixAndGoLayout merchantSlug={merchantSlug}>
      <PixAndGoHeader />
      <PixAndGoStepper currentStep={4} />
      <main className="flex flex-col items-center justify-center flex-1 px-6 pb-12 sm:px-12">
        <div className="w-full max-w-lg text-center flex flex-col items-center gap-6">
          <div className="w-16 h-16 border-4 border-pag-primary border-t-transparent rounded-full animate-spin" />
          <h1 className="text-2xl font-bold text-pag-on-background">
            {i18n.t(
              isDeposit
                ? "pixAndGo.processing.deposit.title"
                : "pixAndGo.processing.registering.title",
            )}
          </h1>
          <p className="text-base text-pag-on-background-muted">
            {i18n.t(
              isDeposit
                ? "pixAndGo.processing.deposit.message"
                : "pixAndGo.processing.registering.message",
            )}
          </p>
        </div>
      </main>
    </PixAndGoLayout>
  );
}

export default PixAndGoProcessing;
